import Button from 'components/crm/ui/Button';
import DateField from 'components/crm/ui/DateField';
import TextField from 'components/crm/ui/TextField';
import { crmTaskErrorText } from 'helper/crmTaskErrors';
import { isoDay } from 'helper/timeLabels';
import useCrmContext from 'hooks/useCrmContext';
import useText from 'hooks/useText';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';

const pad = (value) => String(value).padStart(2, '0');
const clockOf = (date) => `${pad(date.getHours())}:${pad(date.getMinutes())}`;

// The local moment of `day` (YYYY-MM-DD) at `clock` (HH:MM), or null.
const momentOf = (day, clock) => {
  const [year, month, date] = (day ?? '').split('-').map(Number);
  const match = /^(\d{1,2}):(\d{2})$/.exec(clock ?? '');
  if (!year || !month || !date || !match) return null;
  return new Date(year, month - 1, date, Number(match[1]), Number(match[2]));
};

/**
 * « Modifier » of an appointment, in place of its detail: its title, its day,
 * when it starts and ends, its description. Saves through updateAppointment
 * and hands the detail returned to `onSaved`; « Annuler » or Échap give back
 * the detail untouched. A title is required only without a company, as on the
 * api.
 */
const AppointmentEditForm = ({ item, onSaved, onCancel }) => {
  const { text } = useText('appointment');
  const { text: planningText } = useText('planning');
  const { text: tasksText } = useText('tasks');
  const { call } = useCrmContext();
  const [form, setForm] = useState(() => {
    const start = new Date(item.startsAt);
    return {
      title: item.title ?? '',
      day: isoDay(start),
      start: clockOf(start),
      end: clockOf(new Date(item.endsAt)),
      description: item.description ?? '',
    };
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  // Bubbling, after the calendar of the day field: an Échap that closes the
  // calendar (default prevented) leaves the form open.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !event.defaultPrevented)
        cancelRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const set = (field) => (event) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));
  const startsAt = momentOf(form.day, form.start);
  const endsAt = momentOf(form.day, form.end);
  const timeOk = Boolean(startsAt && endsAt && endsAt > startsAt);
  const titleOk =
    Boolean(form.title.trim()) || Boolean(item.companyId || item.company);
  const canSave = timeOk && titleOk && !busy;

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    setError('');
    try {
      const updated = await call(crmApi.updateAppointment, {
        id: item.id,
        fields: {
          title: form.title.trim(),
          startsAt: startsAt.toISOString(),
          endsAt: endsAt.toISOString(),
          description: form.description.trim(),
        },
      });
      toast.success(text('updated'));
      onSaved(updated);
    } catch (failure) {
      const message = crmTaskErrorText(failure, tasksText);
      if (message) setError(message);
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <TextField
        id="appointment-edit-title"
        label={planningText('titleLabel')}
        value={form.title}
        maxLength={200}
        onChange={set('title')}
      />
      <DateField
        id="appointment-edit-day"
        label={text('dayLabel')}
        value={form.day}
        onChange={(day) => setForm((current) => ({ ...current, day }))}
      />
      <div className="grid grid-cols-2 gap-3">
        <TextField
          id="appointment-edit-start"
          type="time"
          label={text('startLabel')}
          value={form.start}
          onChange={set('start')}
        />
        <TextField
          id="appointment-edit-end"
          type="time"
          label={text('endLabel')}
          value={form.end}
          onChange={set('end')}
          error={
            startsAt && endsAt && !timeOk
              ? tasksText('errors.INVALID_APPOINTMENT_TIME')
              : null
          }
        />
      </div>
      <TextField
        id="appointment-edit-description"
        multiline
        rows={4}
        label={planningText('descriptionLabel')}
        value={form.description}
        maxLength={5000}
        onChange={set('description')}
      />
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>
          {text('cancelEdit')}
        </Button>
        <Button disabled={!canSave} onClick={save}>
          {text('saveEdit')}
        </Button>
      </div>
    </div>
  );
};

export default AppointmentEditForm;
